import type { Item, Scene } from '@backend/prisma';
import type {
  GetImageFileUploadResponse,
  GetImageUploadResponse,
  GetVideoUploadResponse,
} from '@stw/types';

import { stwFetch } from '@/utils/stwFetch';

export const getVideoUploadUrl = async (
  itemId: Item['id']
): Promise<{
  path: string;
  signedUrl: string;
}> => {
  const data: GetVideoUploadResponse = await stwFetch(
    'GET',
    `/upload/video/${itemId}`
  );

  const { path, url } = data;

  return {
    path,
    signedUrl: url,
  };
};

export const getImageUploadUrl = async (
  itemId: Item['id']
): Promise<{
  path: string;
  signedUrl: string;
}> => {
  const data: GetImageUploadResponse = await stwFetch(
    'GET',
    `/upload/image/${itemId}`
  );

  const { path, url } = data;

  return {
    path,
    signedUrl: url,
  };
};

export const getImageFileUploadUrl = async (
  itemId: Item['id'],
  filename: string
): Promise<{
  path: string;
  signedUrl: string;
}> => {
  const data: GetImageFileUploadResponse = await stwFetch(
    'GET',
    `/upload/image/${itemId}/file?filename=${encodeURIComponent(filename)}`
  );

  const { path, url } = data;

  return {
    path,
    signedUrl: url,
  };
};

export const startVideoScan = async (
  itemId: string,
  duration: number,
  width: number,
  height: number,
  inputPath: string
) => {
  await stwFetch('POST', `/scan/video/${itemId}`, {
    duration,
    width,
    height,
    input_bucket_path: inputPath,
  });
};

export const startImageScan = async (itemId: string, inputPath: string) => {
  await stwFetch('POST', `/scan/image/${itemId}`, {
    input_bucket_path: inputPath,
  });
};

/**
 * Starts the Meshy image-to-3d pipeline for an item - a separate path from
 * startImageScan (KIRI), not auto-routed. Used by whichever upload flow lets
 * the user explicitly pick Meshy as the generation engine.
 */
export const startImageScanMeshy = async (
  itemId: string,
  inputPath: string
) => {
  await stwFetch('POST', `/scan/image-meshy/${itemId}`, {
    input_bucket_path: inputPath,
  });
};

/**
 * Get signed upload URL for direct item uploads (ready 3D models).
 * For uploading files directly to final storage WITHOUT processing.
 * Supported types: 'model', 'thumbnail', 'ksplat', 'collision'
 */
export const getItemUploadUrl = async (
  itemId: Item['id'],
  uploadType: 'model' | 'thumbnail' | 'ksplat' | 'collision'
): Promise<{
  path: string;
  signedUrl: string;
}> => {
  const queryString = `uploadType=${uploadType}`;

  const { path, url } = await stwFetch(
    'GET',
    `/upload/item/${itemId}?${queryString}`
  );

  return { path, signedUrl: url };
};

export const uploadAndUpdateItem = async (
  itemId: Item['id'],
  file: File,
  uploadType: 'model' | 'thumbnail'
): Promise<Partial<Item>> => {
  const { signedUrl: url } = await getItemUploadUrl(itemId, uploadType);

  await uploadToSignedUrl(url, file);

  return { id: itemId };
};

/**
 * Uploads a scene's thumbnail. Real scene-native route (`GET /upload/scene/:sceneId`) — it
 * happens to write to the same bucket path a same-id room's thumbnail would (see
 * `getSceneThumbnailPath()`'s doc comment on the backend, `backend/src/utils/bucket.ts`), so a
 * backfilled scene's pre-existing thumbnail is found without any bucket copy, but that's a
 * storage-layout detail — this route checks scene ownership directly and needs no backing `room`
 * row to exist.
 */
export const uploadSceneThumbnail = async (
  sceneId: Scene['id'],
  file: File
): Promise<Partial<Scene>> => {
  const { url } = await stwFetch(
    'GET',
    `/upload/scene/${sceneId}?uploadType=thumbnail`
  );
  await uploadToSignedUrl(url, file);
  return { id: sceneId };
};

// Throws on a rejected upload too (fetch only throws on network errors), so callers' cleanup runs.
export const uploadToSignedUrl = async (
  signedUrl: string,
  file: File,
  onProgress?: (fraction: number) => void
): Promise<Response> => {
  if (onProgress) return uploadWithProgress(signedUrl, file, onProgress);
  const response = import.meta.env.DEV
    ? await fetch(signedUrl, {
        method: 'POST',
        headers: { Authorization: 'Firebase owner' },
        body: file,
      })
    : await fetch(signedUrl, {
        method: 'PUT',
        body: file,
      });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(
      `Upload failed: ${response.status} ${response.statusText} ${body}`.trim()
    );
  }
  return response;
};

// fetch can't report upload progress, so XHR here; same request shape as above.
const uploadWithProgress = (
  signedUrl: string,
  file: File,
  onProgress: (fraction: number) => void
): Promise<Response> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(import.meta.env.DEV ? 'POST' : 'PUT', signedUrl);
    if (import.meta.env.DEV)
      xhr.setRequestHeader('Authorization', 'Firebase owner');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(new Response(null, { status: xhr.status }));
      } else {
        reject(
          new Error(
            `Upload failed: ${xhr.status} ${xhr.statusText} ${xhr.responseText}`.trim()
          )
        );
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.send(file);
  });

// Item annotation image upload functions

export const getItemAnnotationImageUploadUrl = async (
  itemId: string,
  annotationId: string,
  filename: string
): Promise<{
  path: string;
  signedUrl: string;
}> => {
  const data = await stwFetch(
    'GET',
    `/upload/item/${itemId}/annotation/${annotationId}?filename=${encodeURIComponent(filename)}`
  );

  const { path, url } = data;

  return {
    path,
    signedUrl: url,
  };
};

export const uploadItemAnnotationImage = async (
  itemId: string,
  annotationId: string,
  file: File
): Promise<string> => {
  const { signedUrl, path } = await getItemAnnotationImageUploadUrl(
    itemId,
    annotationId,
    file.name
  );

  await uploadToSignedUrl(signedUrl, file);

  return path;
};

// Room annotation image upload functions

export const getRoomAnnotationImageUploadUrl = async (
  roomId: string,
  annotationId: string,
  filename: string
): Promise<{
  path: string;
  signedUrl: string;
}> => {
  const data = await stwFetch(
    'GET',
    `/upload/room/${roomId}/annotation/${annotationId}?filename=${encodeURIComponent(filename)}`
  );

  const { path, url } = data;

  return {
    path,
    signedUrl: url,
  };
};

export const uploadRoomAnnotationImage = async (
  roomId: string,
  annotationId: string,
  file: File
): Promise<string> => {
  const { signedUrl, path } = await getRoomAnnotationImageUploadUrl(
    roomId,
    annotationId,
    file.name
  );

  await uploadToSignedUrl(signedUrl, file);

  return path;
};

// Scene annotation image upload functions

export const getSceneAnnotationImageUploadUrl = async (
  sceneId: string,
  annotationId: string,
  filename: string
): Promise<{
  path: string;
  signedUrl: string;
}> => {
  const data = await stwFetch(
    'GET',
    `/upload/scene/${sceneId}/annotation/${annotationId}?filename=${encodeURIComponent(filename)}`
  );

  const { path, url } = data;

  return {
    path,
    signedUrl: url,
  };
};

export const uploadSceneAnnotationImage = async (
  sceneId: string,
  annotationId: string,
  file: File
): Promise<string> => {
  const { signedUrl, path } = await getSceneAnnotationImageUploadUrl(
    sceneId,
    annotationId,
    file.name
  );

  await uploadToSignedUrl(signedUrl, file);

  return path;
};
